import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Settings, Key, Database, Sliders, Save, TestTube,
  Loader2, Check, AlertCircle, Eye, EyeOff, Mail, Phone,
  RefreshCw, Link2, AlertTriangle
} from 'lucide-react';

export default function SettingsPage() {
  const { isAdmin, user } = useAuth();
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);

  // Determine visible tabs based on permissions
  const canCustomApis = isAdmin || (user && user.custom_api_keys);
  const canCustomQdrant = isAdmin || (user && user.custom_qdrant);

  // API Keys
  const [openaiKey, setOpenaiKey] = useState('');
  const [deepgramKey, setDeepgramKey] = useState('');
  const [showOpenai, setShowOpenai] = useState(false);
  const [showDeepgram, setShowDeepgram] = useState(false);
  const [existingKeys, setExistingKeys] = useState([]);

  // Qdrant
  const [qdrantUrl, setQdrantUrl] = useState('');
  const [qdrantApiKey, setQdrantApiKey] = useState('');
  const [qdrantCollection, setQdrantCollection] = useState('product_sheets');
  const [qdrantTestResult, setQdrantTestResult] = useState(null);
  const [testingQdrant, setTestingQdrant] = useState(false);

  // Analysis
  const [wordThreshold, setWordThreshold] = useState(150);
  const [timeInterval, setTimeInterval] = useState(30);
  const [contextWindow, setContextWindow] = useState(2000);
  const [autoSave, setAutoSave] = useState(true);
  const [defaultAgent, setDefaultAgent] = useState('Agente');
  const [defaultClient, setDefaultClient] = useState('Cliente');

  // SMTP
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', use_tls: true });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  // Twilio
  const [twilio, setTwilio] = useState({ account_sid: '', phone_number: '', webhook_base_url: '', welcome_message: '', stream_track: 'both_tracks', default_agent_name: 'Agente', default_campaign_id: '' });
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [twilioTestResult, setTwilioTestResult] = useState(null);
  const [testingTwilio, setTestingTwilio] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [syncingWebhooks, setSyncingWebhooks] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [settingUpTwimlApp, setSettingUpTwimlApp] = useState(false);
  const [twimlAppResult, setTwimlAppResult] = useState(null);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Fetch all settings in parallel, always in fixed positions
      const [keys, qdrantData, analysisData, smtpData, twilioData] = await Promise.all([
        canCustomApis ? api.get('/api/settings/apikeys') : Promise.resolve([]),
        canCustomQdrant ? api.get('/api/settings/qdrant') : Promise.resolve({}),
        api.get('/api/settings/analysis'),
        isAdmin ? api.get('/api/admin/smtp').catch(() => ({})) : Promise.resolve({}),
        api.get('/api/settings/twilio').catch(() => ({})),
      ]);
      setExistingKeys(keys);
      if (qdrantData) {
        setQdrantUrl(qdrantData.url || '');
        setQdrantCollection(qdrantData.collection_name || 'product_sheets');
      }
      if (analysisData) {
        setWordThreshold(analysisData.word_threshold || 150);
        setTimeInterval(analysisData.time_interval || 30);
        setContextWindow(analysisData.context_window || 2000);
        setAutoSave(analysisData.auto_save !== false);
        setDefaultAgent(analysisData.default_speaker_agent || 'Agente');
        setDefaultClient(analysisData.default_speaker_client || 'Cliente');
      }
      if (smtpData && smtpData.host) setSmtp(smtpData);
      if (twilioData && twilioData.account_sid) {
        setTwilio({
          account_sid: twilioData.account_sid || '',
          phone_number: twilioData.phone_number || '',
          webhook_base_url: twilioData.webhook_base_url || '',
          welcome_message: twilioData.welcome_message || '',
          stream_track: twilioData.stream_track || 'both_tracks',
          default_agent_name: twilioData.default_agent_name || 'Agente',
          default_campaign_id: twilioData.default_campaign_id || '',
          browser_calling_enabled: twilioData.browser_calling_enabled || false,
          twiml_app_sid: twilioData.twiml_app_sid || '',
          api_key_sid: twilioData.api_key_sid || '',
        });
        setTwilioConfigured(!!twilioData.auth_token_preview);
      }
      // Load webhook sync status
      try {
        const whStatus = await api.get('/api/settings/twilio/webhook-status');
        setWebhookStatus(whStatus);
      } catch (_) {}
    } catch (err) {
      toast.error('Error al cargar configuracion');
    }
    setLoading(false);
  };

  const saveApiKey = async (serviceName, keyValue) => {
    if (!keyValue) return;
    try {
      await api.post('/api/settings/apikeys', { service_name: serviceName, key_value: keyValue });
      toast.success(`Clave de ${serviceName} guardada`);
      if (serviceName === 'openai') setOpenaiKey('');
      if (serviceName === 'deepgram') setDeepgramKey('');
      loadSettings();
    } catch (err) { toast.error('Error al guardar clave'); }
  };

  const saveQdrant = async () => {
    try {
      await api.put('/api/settings/qdrant', {
        url: qdrantUrl || undefined,
        api_key: qdrantApiKey || undefined,
        collection_name: qdrantCollection || undefined,
      });
      toast.success('Configuracion de Qdrant guardada');
      setQdrantApiKey('');
    } catch (err) { toast.error('Error al guardar'); }
  };

  const testQdrant = async () => {
    setTestingQdrant(true);
    setQdrantTestResult(null);
    try {
      const result = await api.post('/api/settings/qdrant/test');
      setQdrantTestResult(result);
    } catch (err) { setQdrantTestResult({ status: 'error', message: err.message }); }
    setTestingQdrant(false);
  };

  const saveAnalysis = async () => {
    try {
      await api.put('/api/settings/analysis', {
        word_threshold: wordThreshold, time_interval: timeInterval,
        context_window: contextWindow, auto_save: autoSave,
        default_speaker_agent: defaultAgent, default_speaker_client: defaultClient,
      });
      toast.success('Configuracion de analisis guardada');
    } catch (err) { toast.error('Error al guardar'); }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await api.post('/api/admin/smtp', smtp);
      toast.success('Configuracion SMTP guardada');
    } catch (err) { toast.error(err.message); }
    setSavingSmtp(false);
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const result = await api.post('/api/admin/smtp/test', {});
      toast.success(result.message || 'Conexion exitosa');
    } catch (err) { toast.error(err.message); }
    setTestingSmtp(false);
  };

  const saveTwilio = async () => {
    try {
      const payload = { ...twilio };
      if (twilioAuthToken) payload.auth_token = twilioAuthToken;
      await api.put('/api/settings/twilio', payload);
      toast.success('Configuracion de Twilio guardada');
      setTwilioAuthToken('');
      loadSettings();
    } catch (err) { toast.error('Error al guardar configuracion Twilio'); }
  };

  const testTwilio = async () => {
    setTestingTwilio(true);
    setTwilioTestResult(null);
    try {
      const result = await api.post('/api/settings/twilio/test');
      setTwilioTestResult(result);
    } catch (err) { setTwilioTestResult({ status: 'error', message: err.message }); }
    setTestingTwilio(false);
  };

  const syncWebhooks = async () => {
    setSyncingWebhooks(true);
    setSyncResult(null);
    try {
      const result = await api.post('/api/settings/twilio/sync-webhooks');
      setSyncResult(result);
      if (result.status === 'ok') {
        toast.success('Webhooks sincronizados con Twilio');
        // Refresh status
        const whStatus = await api.get('/api/settings/twilio/webhook-status');
        setWebhookStatus(whStatus);
      }
    } catch (err) { setSyncResult({ status: 'error', message: err.message }); }
    setSyncingWebhooks(false);
  };

  const setupTwimlApp = async () => {
    setSettingUpTwimlApp(true);
    setTwimlAppResult(null);
    try {
      const result = await api.post('/api/settings/twilio/setup-twiml-app');
      setTwimlAppResult(result);
      if (result.status === 'ok') {
        toast.success('TwiML App configurada correctamente');
        loadSettings();
      }
    } catch (err) { setTwimlAppResult({ status: 'error', message: err.message }); }
    setSettingUpTwimlApp(false);
  };

  const tabs = [];
  if (canCustomApis) tabs.push({ id: 'apikeys', label: 'Claves API', icon: Key });
  if (canCustomQdrant) tabs.push({ id: 'qdrant', label: 'Qdrant', icon: Database });
  tabs.push({ id: 'analysis', label: 'Analisis', icon: Sliders });
  tabs.push({ id: 'twilio', label: 'Twilio Voice', icon: Phone });
  if (isAdmin) tabs.push({ id: 'smtp', label: 'Correo SMTP', icon: Mail });

  // Set default active tab once tabs are determined
  useEffect(() => {
    if (tabs.length > 0 && !activeTab) {
      setActiveTab(tabs[0].id);
    }
  }, [canCustomApis, canCustomQdrant]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasKey = (name) => existingKeys.some(k => k.service_name === name);

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Configuracion</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Gestiona claves API, conexion a Qdrant, analisis y correo</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ backgroundColor: 'var(--surface-secondary)' }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? 'text-white shadow-md' : ''}`}
              style={{ backgroundColor: isActive ? 'var(--accent-primary)' : 'transparent', color: isActive ? '#fff' : 'var(--text-muted)' }}>
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : (
        <>
          {/* API Keys Tab */}
          {activeTab === 'apikeys' && canCustomApis && (
            <div className="space-y-4" data-testid="apikeys-panel">
              <ApiKeyCard label="OpenAI API Key" description="Para GPT-4o y embeddings."
                value={openaiKey} onChange={setOpenaiKey} onSave={() => saveApiKey('openai', openaiKey)}
                hasExisting={hasKey('openai')} show={showOpenai} onToggleShow={() => setShowOpenai(!showOpenai)} testId="openai" />
              <ApiKeyCard label="Deepgram API Key" description="Para transcripcion de voz en tiempo real."
                value={deepgramKey} onChange={setDeepgramKey} onSave={() => saveApiKey('deepgram', deepgramKey)}
                hasExisting={hasKey('deepgram')} show={showDeepgram} onToggleShow={() => setShowDeepgram(!showDeepgram)} testId="deepgram" />
            </div>
          )}

          {/* Qdrant Tab */}
          {activeTab === 'qdrant' && canCustomQdrant && (
            <div className="rounded-xl border p-6 space-y-4" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="qdrant-panel">
              <div>
                <h3 className="font-heading font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>Conexion a Qdrant</h3>
                <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Configura tu instancia de Qdrant externa para busqueda vectorial</p>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>URL</label>
                <input type="text" value={qdrantUrl} onChange={e => setQdrantUrl(e.target.value)} data-testid="qdrant-url-input" placeholder="http://tu-servidor:6333"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>API Key</label>
                <input type="password" value={qdrantApiKey} onChange={e => setQdrantApiKey(e.target.value)} data-testid="qdrant-apikey-input" placeholder="Deja vacio si no requiere autenticacion"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nombre de Coleccion</label>
                <input type="text" value={qdrantCollection} onChange={e => setQdrantCollection(e.target.value)} data-testid="qdrant-collection-input"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button onClick={saveQdrant} data-testid="save-qdrant-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                  <Save size={16} /> Guardar
                </button>
                <button onClick={testQdrant} disabled={testingQdrant} data-testid="test-qdrant-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                  {testingQdrant ? <Loader2 size={16} className="animate-spin" /> : <TestTube size={16} />} Probar Conexion
                </button>
              </div>
              {qdrantTestResult && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${qdrantTestResult.status === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`} data-testid="qdrant-test-result">
                  {qdrantTestResult.status === 'ok' ? <Check size={16} /> : <AlertCircle size={16} />} {qdrantTestResult.message}
                </div>
              )}
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === 'analysis' && (
            <div className="rounded-xl border p-6 space-y-5" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="analysis-panel">
              <div>
                <h3 className="font-heading font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>Parametros de Analisis</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Configura cuando y como se activa el analisis IA durante una llamada</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Umbral de Palabras</label>
                  <input type="number" value={wordThreshold} onChange={e => setWordThreshold(parseInt(e.target.value) || 0)} data-testid="word-threshold-input"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Palabras nuevas para activar analisis</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Intervalo (segundos)</label>
                  <input type="number" value={timeInterval} onChange={e => setTimeInterval(parseInt(e.target.value) || 0)} data-testid="time-interval-input"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Tiempo maximo entre analisis</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Ventana de Contexto</label>
                  <input type="number" value={contextWindow} onChange={e => setContextWindow(parseInt(e.target.value) || 0)} data-testid="context-window-input"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Tokens de contexto enviados a GPT-4o</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Etiqueta Agente por defecto</label>
                  <input type="text" value={defaultAgent} onChange={e => setDefaultAgent(e.target.value)} data-testid="default-agent-input"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Etiqueta Cliente por defecto</label>
                  <input type="text" value={defaultClient} onChange={e => setDefaultClient(e.target.value)} data-testid="default-client-input"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setAutoSave(!autoSave)} data-testid="auto-save-toggle" className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {autoSave ? (
                    <div className="w-10 h-5 rounded-full bg-green-500 relative"><div className="w-4 h-4 bg-white rounded-full absolute right-0.5 top-0.5" /></div>
                  ) : (
                    <div className="w-10 h-5 rounded-full relative" style={{ backgroundColor: 'var(--border-color)' }}><div className="w-4 h-4 bg-white rounded-full absolute left-0.5 top-0.5" /></div>
                  )}
                  Guardado automatico de sesiones
                </button>
              </div>
              <button onClick={saveAnalysis} data-testid="save-analysis-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                <Save size={16} /> Guardar Configuracion
              </button>
            </div>
          )}

          {/* Twilio Voice Tab */}
          {activeTab === 'twilio' && (
            <div className="rounded-xl border p-6 space-y-4" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="twilio-panel">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Twilio Programmable Voice</h3>
                  {twilioConfigured && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-green-500/20 text-green-400">Configurado</span>}
                </div>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Conecta llamadas telefonicas en vivo al pipeline de analisis IA via Twilio Media Streams</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Account SID</label>
                  <input type="text" value={twilio.account_sid} onChange={e => setTwilio(p => ({ ...p, account_sid: e.target.value }))} data-testid="twilio-account-sid" placeholder="ACxxxxxxxxxxxxxxx"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Auth Token</label>
                  <div className="relative">
                    <input type={showTwilioToken ? 'text' : 'password'} value={twilioAuthToken} onChange={e => setTwilioAuthToken(e.target.value)} data-testid="twilio-auth-token"
                      placeholder={twilioConfigured ? '***configurado*** (dejar vacio para mantener)' : 'Token de autenticacion'}
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono pr-10" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    <button onClick={() => setShowTwilioToken(!showTwilioToken)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                      {showTwilioToken ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Numero Twilio</label>
                  <input type="text" value={twilio.phone_number} onChange={e => setTwilio(p => ({ ...p, phone_number: e.target.value }))} data-testid="twilio-phone" placeholder="+34612345678"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>URL Base del Webhook</label>
                  <input type="text" value={twilio.webhook_base_url} onChange={e => setTwilio(p => ({ ...p, webhook_base_url: e.target.value }))} data-testid="twilio-webhook-url" placeholder="https://tu-dominio.com"
                    className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>URL publica accesible por Twilio (sin /api al final)</p>
                </div>
              </div>
              <div className="pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Parametros de la llamada</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nombre de agente por defecto</label>
                    <input type="text" value={twilio.default_agent_name} onChange={e => setTwilio(p => ({ ...p, default_agent_name: e.target.value }))} data-testid="twilio-agent-name"
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>ID de Campaña</label>
                    <input type="text" value={twilio.default_campaign_id} onChange={e => setTwilio(p => ({ ...p, default_campaign_id: e.target.value }))} data-testid="twilio-campaign"
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Track de audio</label>
                    <select value={twilio.stream_track} onChange={e => setTwilio(p => ({ ...p, stream_track: e.target.value }))} data-testid="twilio-track"
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                      <option value="both_tracks">Ambas pistas (inbound + outbound)</option>
                      <option value="inbound_track">Solo entrante</option>
                      <option value="outbound_track">Solo saliente</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Mensaje de bienvenida</label>
                    <input type="text" value={twilio.welcome_message} onChange={e => setTwilio(p => ({ ...p, welcome_message: e.target.value }))} data-testid="twilio-welcome"
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button onClick={saveTwilio} data-testid="save-twilio-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                  <Save size={16} /> Guardar
                </button>
                <button onClick={testTwilio} disabled={testingTwilio} data-testid="test-twilio-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                  {testingTwilio ? <Loader2 size={16} className="animate-spin" /> : <TestTube size={16} />} Probar Conexion
                </button>
              </div>
              {twilioTestResult && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${twilioTestResult.status === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`} data-testid="twilio-test-result">
                  {twilioTestResult.status === 'ok' ? <Check size={16} /> : <AlertCircle size={16} />} {twilioTestResult.message}
                </div>
              )}
              {twilio.webhook_base_url && (
                <div className="space-y-3">
                  {/* Webhook Sync Status */}
                  <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Link2 size={14} style={{ color: 'var(--accent-primary)' }} />
                        <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Sincronizacion con Twilio</p>
                      </div>
                      {webhookStatus && (
                        webhookStatus.synced ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-green-500/20 text-green-400 flex items-center gap-1">
                            <Check size={10} /> Sincronizado
                          </span>
                        ) : webhookStatus.needs_resync ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-amber-500/20 text-amber-400 flex items-center gap-1">
                            <AlertTriangle size={10} /> Dominio cambiado
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-gray-500/20 text-gray-400">
                            No sincronizado
                          </span>
                        )
                      )}
                    </div>
                    {webhookStatus && webhookStatus.needs_resync && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 bg-amber-500/10 text-amber-400 text-xs">
                        <AlertTriangle size={14} />
                        <span>El dominio cambio desde la ultima sincronizacion ({webhookStatus.synced_url} → {webhookStatus.current_url}). Resincroniza para aplicar.</span>
                      </div>
                    )}
                    {webhookStatus && webhookStatus.synced && !webhookStatus.needs_resync && webhookStatus.synced_at && (
                      <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
                        Ultima sincronizacion: {new Date(webhookStatus.synced_at).toLocaleString('es-ES')} — {webhookStatus.phone_number}
                      </p>
                    )}
                    <button onClick={syncWebhooks} disabled={syncingWebhooks} data-testid="sync-webhooks-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-lg"
                      style={{ backgroundColor: '#8b5cf6' }}>
                      {syncingWebhooks ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      {syncingWebhooks ? 'Sincronizando...' : webhookStatus?.synced && !webhookStatus?.needs_resync ? 'Resincronizar Webhooks' : 'Sincronizar Webhooks con Twilio'}
                    </button>
                    <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>
                      Configura automaticamente el numero de Twilio para que apunte a este servidor. No necesitas ir a la consola de Twilio.
                    </p>
                  </div>

                  {syncResult && (
                    <div className={`flex items-start gap-2 px-4 py-3 rounded-lg text-sm ${syncResult.status === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`} data-testid="sync-result">
                      {syncResult.status === 'ok' ? <Check size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
                      <div>
                        <p>{syncResult.message}</p>
                        {syncResult.voice_url && <p className="text-[10px] font-mono mt-1 opacity-75">Voice URL: {syncResult.voice_url}</p>}
                      </div>
                    </div>
                  )}

                  {/* Endpoints Reference */}
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--background)' }}>
                    <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Endpoints configurados</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold bg-blue-500/20 text-blue-400">POST</span>
                        <code className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{twilio.webhook_base_url}/api/twilio/voice/incoming</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold bg-blue-500/20 text-blue-400">POST</span>
                        <code className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{twilio.webhook_base_url}/api/twilio/voice/outbound</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold bg-green-500/20 text-green-400">WSS</span>
                        <code className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{twilio.webhook_base_url.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/twilio-media</code>
                      </div>
                    </div>
                  </div>

                  {/* TwiML App Setup (for browser calling) */}
                  <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Phone size={14} style={{ color: '#22c55e' }} />
                        <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Llamadas desde el navegador</p>
                      </div>
                      {twilio.browser_calling_enabled ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-green-500/20 text-green-400 flex items-center gap-1">
                          <Check size={10} /> Activo
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-gray-500/20 text-gray-400">
                          No configurado
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
                      Crea una TwiML App y API Key en tu cuenta Twilio para poder realizar y recibir llamadas desde el navegador.
                      {twilio.twiml_app_sid && <><br/>TwiML App: <code className="font-mono">{twilio.twiml_app_sid}</code></>}
                      {twilio.api_key_sid && <><br/>API Key: <code className="font-mono">{twilio.api_key_sid}</code></>}
                    </p>
                    <button onClick={setupTwimlApp} disabled={settingUpTwimlApp} data-testid="setup-twiml-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-lg"
                      style={{ backgroundColor: '#22c55e' }}>
                      {settingUpTwimlApp ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />}
                      {twilio.browser_calling_enabled ? 'Reconfigurar TwiML App' : 'Configurar TwiML App'}
                    </button>
                    {twimlAppResult && (
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs mt-2 ${twimlAppResult.status === 'ok' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                        {twimlAppResult.status === 'ok' ? <Check size={14} /> : <AlertCircle size={14} />} {twimlAppResult.message}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SMTP Tab */}
          {activeTab === 'smtp' && isAdmin && (
            <div className="rounded-xl border p-6 space-y-4" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="smtp-panel">
              <div>
                <h3 className="font-heading font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>Servidor de Correo SMTP</h3>
                <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Configura el servidor de correo para recuperacion de contraseñas y notificaciones</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Servidor SMTP</label>
                  <input type="text" value={smtp.host} onChange={e => setSmtp(p => ({ ...p, host: e.target.value }))} data-testid="smtp-host" placeholder="smtp.gmail.com"
                    className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Puerto</label>
                  <input type="number" value={smtp.port} onChange={e => setSmtp(p => ({ ...p, port: parseInt(e.target.value) || 587 }))} data-testid="smtp-port"
                    className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none font-mono" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Usuario</label>
                  <input type="text" value={smtp.username} onChange={e => setSmtp(p => ({ ...p, username: e.target.value }))} data-testid="smtp-username"
                    className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Contraseña</label>
                  <input type="password" value={smtp.password} onChange={e => setSmtp(p => ({ ...p, password: e.target.value }))} data-testid="smtp-password"
                    className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Email remitente</label>
                  <input type="email" value={smtp.from_email} onChange={e => setSmtp(p => ({ ...p, from_email: e.target.value }))} data-testid="smtp-from" placeholder="noreply@tuempresa.com"
                    className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" checked={smtp.use_tls} onChange={e => setSmtp(p => ({ ...p, use_tls: e.target.checked }))} data-testid="smtp-tls" />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Usar TLS</span>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <button onClick={saveSmtp} disabled={savingSmtp} data-testid="smtp-save-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                  {savingSmtp ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
                </button>
                <button onClick={testSmtp} disabled={testingSmtp} data-testid="smtp-test-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
                  {testingSmtp ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Probar Conexion
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ApiKeyCard({ label, description, value, onChange, onSave, hasExisting, show, onToggleShow, testId }) {
  return (
    <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid={`apikey-card-${testId}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{label}</h4>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{description}</p>
        </div>
        {hasExisting && (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-green-500/20 text-green-400">Configurada</span>
        )}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} data-testid={`${testId}-key-input`} placeholder="sk-..."
            className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none font-mono pr-10" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <button onClick={onToggleShow} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        <button onClick={onSave} data-testid={`save-${testId}-key`} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
          <Save size={14} /> Guardar
        </button>
      </div>
    </div>
  );
}
